import styled, { css } from "styled-components";
import { spacing, typographyMixin } from "@magicbi/ui-library";
import { duration, easing } from "../../../design-system/tokens/transitions";
import type { DurationBarTone } from "./DurationBars.types";

export const Root = styled.figure`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
  margin: 0;
  min-width: 0;
`;

export const Plot = styled.ol<{ $heightRem: number }>`
  display: flex;
  align-items: flex-end;
  gap: 0.25rem;
  height: ${({ $heightRem }) => $heightRem}rem;
  margin: 0;
  padding: 0;
  list-style: none;
`;

const toneColor = (tone: DurationBarTone) => css`
  background-color: ${({ theme }) =>
    tone === "failed" ? theme.colors.error.text : tone === "slow" ? theme.colors.warning.text : tone === "running" ? theme.colors.text.tertiary : theme.colors.success.text};
`;

export const Bar = styled.li<{ $tone: DurationBarTone; $heightPct: number }>`
  flex: 1 1 0;
  min-width: 0.25rem;
  height: ${({ $heightPct }) => $heightPct}%;
  min-height: 0.25rem;
  border-radius: 0.125rem 0.125rem 0 0;
  ${({ $tone }) => toneColor($tone)}
  transform-origin: bottom;
  transition: height ${duration.normal} ${easing.smooth};
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const Axis = styled.div`
  display: flex;
  justify-content: space-between;
  ${typographyMixin("caption", 4)}
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const Legend = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[3]};
  margin: 0;
  padding: 0;
  list-style: none;
  ${typographyMixin("caption", 3)}
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const LegendItem = styled.li<{ $tone: DurationBarTone }>`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]};
  &::before {
    content: "";
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 0.125rem;
    ${({ $tone }) => toneColor($tone)}
  }
`;

export const Note = styled.p`
  margin: 0;
  ${typographyMixin("caption", 3)}
  color: ${({ theme }) => theme.colors.error.text};
`;

export const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
`;
